import { useState, useEffect } from 'react';
import { X, AlertCircle, Loader } from 'lucide-react';
import { createClient } from '../../api/api';
import './AddClientModal.css';

function Toggle({ value, onChange }) {
  return (
    <button
      type="button"
      className={`modal-toggle ${value ? 'on' : 'off'}`}
      onClick={() => onChange(!value)}
      aria-pressed={value}
    >
      <span className="modal-toggle-thumb" />
    </button>
  );
}

function AddClientModal({ onClose, onSuccess }) {
  const [idClient,  setIdClient]  = useState('');
  const [subClient, setSubClient] = useState('');
  const [hasApi,    setHasApi]    = useState(true);
  const [error,     setError]     = useState('');
  const [loading,   setLoading]   = useState(false);

  // Pré-remplit le sous-identifiant avec l'identifiant si pas encore modifié
  const [subTouched, setSubTouched] = useState(false);

  useEffect(() => {
    if (!subTouched) {
      setSubClient(idClient);
    }
  }, [idClient, subTouched]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  function sanitize(value) {
    return value.toLowerCase().replace(/[^a-z0-9-]/g, '');
  }

  function handleSubChange(value) {
    setSubTouched(true);
    setSubClient(sanitize(value));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!idClient.trim()) {
      setError('L\'identifiant client est requis.');
      return;
    }

    setLoading(true);

    try {
      const result = await createClient({
        idClient:  idClient.trim(),
        subClient: subClient.trim() !== idClient.trim() ? subClient.trim() : null,
        hasApi,
      });

      if (!result.success) {
        setError(result.message);
        return;
      }

      onSuccess(result.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la création.');
    } finally {
      setLoading(false);
    }
  }

  const effectiveSub  = subClient || idClient;
  const siteUrl       = idClient ? `https://${idClient}.diesesoftware.com` : null;
  const apiUrl        = idClient ? `https://${idClient}.diesesoftware.com/_app/${effectiveSub}/api/authentication/` : null;
  const subDiffers    = subClient && subClient !== idClient;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>

        <div className="modal-header">
          <div>
            <div className="modal-title">Ajouter un client</div>
            <div className="modal-subtitle">Le monitoring démarrera au prochain cycle automatique</div>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Fermer">
            <X size={14} />
          </button>
        </div>

        <form className="modal-form" onSubmit={handleSubmit}>

          {error && (
            <div className="modal-error">
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          {/* Identifiant principal */}
          <div className="modal-field">
            <label className="modal-label" htmlFor="idClient">Identifiant client</label>
            <input
              id="idClient"
              className="modal-input"
              type="text"
              placeholder="ex: carnegiehall"
              value={idClient}
              onChange={e => setIdClient(sanitize(e.target.value))}
              required
              autoFocus
            />
            <span className="modal-hint">Uniquement lettres minuscules, chiffres et tirets</span>
          </div>

          {/* Sous-identifiant */}
          <div className="modal-field">
            <label className="modal-label" htmlFor="subClient">
              Sous-identifiant
              {!subDiffers && (
                <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6, fontSize: '0.72rem' }}>
                  (identique à l'identifiant)
                </span>
              )}
            </label>
            <input
              id="subClient"
              className="modal-input"
              type="text"
              placeholder={idClient || 'ex: carnegiehall-sub'}
              value={subClient}
              onChange={e => handleSubChange(e.target.value)}
            />
            <span className="modal-hint">Utilisé dans l'URL API — modifiable si différent du client</span>
          </div>

          {/* Aperçu URLs */}
          {siteUrl && (
            <div className="modal-field">
              <label className="modal-label">URLs surveillées</label>
              <div className="modal-preview">
                <div style={{ marginBottom: 4 }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>Site web</span><br />
                  <strong>{siteUrl}/</strong>
                </div>
                {hasApi && (
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 6, marginTop: 6 }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>API</span><br />
                    <strong>{apiUrl}</strong>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Toggle API */}
          <div className="modal-toggle-row">
            <div>
              <div className="modal-toggle-label">Surveiller l'API</div>
            </div>
            <Toggle value={hasApi} onChange={setHasApi} />
          </div>

          <div className="modal-footer">
            <button type="button" className="modal-btn-cancel" onClick={onClose}>
              Annuler
            </button>
            <button
              type="submit"
              className="modal-btn-submit"
              disabled={loading || !idClient.trim()}
            >
              {loading
                ? <span style={{ display:'flex', alignItems:'center', gap:6 }}><Loader size={13} className="spin" /> Création…</span>
                : 'Ajouter le client'
              }
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}

export default AddClientModal;
